import React from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

/** Heart button — only rendered for customers and anonymous users.
 * Anon click → login toast. Non-customer roles → hidden. */
export function HeartButton({ post, className = "", size = 18, showCount = true }) {
    const { user, isFavorited, toggleFavorite } = useAuth();
    const nav = useNavigate();
    const isAnon = !user;
    const isCustomer = user?.role === "CUSTOMER";

    // Hide entirely for non-customer roles (SHOP_OWNER, admin, etc.)
    if (user && !isCustomer) return null;

    const filled = isCustomer && isFavorited(post.id);
    const count = Number(post.favorite_count || 0);

    const onClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isAnon) {
            toast("Sign in to save favorites", {
                description: "Create a free customer account to build your list.",
                action: { label: "Sign in", onClick: () => nav("/customer/login") },
            });
            return;
        }
        try {
            const wasFav = filled;
            await toggleFavorite(post.id);
            // Reflect the incremental change on the post prop optimistically.
            if (post.favorite_count != null) {
                post.favorite_count = Math.max(0, count + (wasFav ? -1 : +1));
            }
            if (!wasFav) {
                toast.success("Added to favorites");
            }
        } catch {
            toast.error("Could not update favorite");
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={filled ? "Remove from favorites" : "Save to favorites"}
            data-testid={`heart-btn-${post.id}`}
            data-filled={filled ? "1" : "0"}
            className={`inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[11px] font-semibold shadow-sm backdrop-blur transition hover:scale-105 ${className}`}
        >
            <Heart
                size={size}
                className={filled ? "fill-rose-500 stroke-rose-500" : "stroke-neutral-700"}
            />
            {showCount && count > 0 && (
                <span className={filled ? "text-rose-600" : "text-neutral-700"}>{count}</span>
            )}
        </button>
    );
}

export default HeartButton;
